import React, { FC, useRef } from 'react'
import { useLocation } from 'react-router-dom'

import { Tabs, makeStyles, Grid } from '@material-ui/core'

import { encodePlayerLayerParams, decodePlayerLayerParams } from 'components/PlayerLayer/lib'
import useElementWidth from 'hooks/useElementWidth'
import { PlayerProfileResponse } from 'protos'
import T from 'components/FormattedMessage'
import NavTab from 'components/NavTab'

import { PlayerProfileHeader } from './components'
import { PointspreadTab, TimelineTab, HistoryTab } from './tabs'
import { PlayerLayerTab } from '../../types'

type Props = {
  playerProfile: PlayerProfileResponse
  onBackClick: () => void
}

const useStyles = makeStyles(({ palette, breakpoints, spacing }) => {
  return {
    root: {
      [breakpoints.only('xs')]: {
        minHeight: '100vh',
      },
    },
    solidBackground: {
      backgroundColor: palette.common.white,
    },
    content: {
      backgroundImage: 'linear-gradient(to bottom, #324F6F, #1E2C3F)',
      color: palette.secondary.light,
      flex: 1,
      paddingBottom: spacing(4),
    },
  }
})

const PlayerProfile: FC<Props> = ({
  playerProfile,
  onBackClick,
}) => {
  const classes = useStyles()

  const location = useLocation()
  const search = new URLSearchParams(location.search)
  const { activeTab, ...layerParams } = decodePlayerLayerParams(search.get('playerLayer'))

  const getLinkForTab = (tab: PlayerLayerTab) => {
    search.set('playerLayer', encodePlayerLayerParams({ ...layerParams, activeTab: tab })!)

    return location.pathname + '?' + search.toString()
  }

  const rootRef = useRef<HTMLDivElement | null>(null)
  const contentWidth = useElementWidth(rootRef)

  if (!playerProfile.player) {
    return null
  }

  const simplified = !!layerParams.matchId

  return (
    <Grid
      container
      ref={rootRef}
      direction="column"
      className={classes.root}
    >
      <PlayerProfileHeader
        profile={playerProfile}
        onBackClick={onBackClick}
        substitutionLink={(
          playerProfile.player.substitutionEnabled
            ? `/games/${layerParams.gameId}/sub/${layerParams.playerId}`
            : undefined
        )}
      />

      {!simplified && (
        <Tabs
          variant="fullWidth"
          textColor="inherit"
          indicatorColor="primary"
          value={activeTab}
          className={classes.solidBackground}
        >
          <NavTab
            replace
            value="pointspread"
            to={getLinkForTab('pointspread')}
            label={<T id="playerprofile_pointspread_tab" />}
          />
          <NavTab
            replace
            value="timeline"
            to={getLinkForTab('timeline')}
            label={<T id="playerprofile_timeline_tab" />}
          />
          <NavTab
            replace
            value="history"
            to={getLinkForTab('history')}
            label={<T id="playerprofile_history_tab" />}
          />
        </Tabs>
      )}

      <div className={classes.content}>
        {!simplified && activeTab === 'pointspread' && <PointspreadTab profile={playerProfile} />}
        {!simplified && activeTab === 'timeline' && <TimelineTab profile={playerProfile} contentWidth={contentWidth} />}
        {(simplified || activeTab === 'history') && <HistoryTab profile={playerProfile} />}
      </div>
    </Grid>
  )
}

export default PlayerProfile
try {
    // @ts-ignore
    PlayerProfile.displayName = "PlayerProfile";
    // @ts-ignore
    PlayerProfile.__docgenInfo = { "description": "", "displayName": "PlayerProfile", "props": { "playerProfile": { "defaultValue": null, "description": "", "name": "playerProfile", "required": true, "type": { "name": "any" } }, "onBackClick": { "defaultValue": null, "description": "", "name": "onBackClick", "required": true, "type": { "name": "() => void" } } } };
    // @ts-ignore
    if (typeof STORYBOOK_REACT_CLASSES !== "undefined")
        // @ts-ignore
        STORYBOOK_REACT_CLASSES["src/components/PlayerLayer/components/PlayerProfile/PlayerProfile.tsx#PlayerProfile"] = { docgenInfo: PlayerProfile.__docgenInfo, name: "PlayerProfile", path: "src/components/PlayerLayer/components/PlayerProfile/PlayerProfile.tsx#PlayerProfile" };
}
catch (__react_docgen_typescript_loader_error) { }